import { useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { Bold, Italic, List, Eye, Pencil } from 'lucide-react';

export const MarkdownContent = ({ text }) => (
  <div className="prose prose-sm dark:prose-invert max-w-none prose-p:my-1.5 prose-ul:my-1.5 prose-li:my-0.5">
    <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{text || ''}</ReactMarkdown>
  </div>
);

// Markdown-Eingabefeld mit Toolbar (Fett/Kursiv/Liste) + Umschalt Bearbeiten/Vorschau.
export const MarkdownField = ({ value, onChange, placeholder, testId }) => {
  const ref = useRef(null);
  const [preview, setPreview] = useState(false);

  const wrap = (before, after = before) => {
    const ta = ref.current;
    if (!ta) return;
    const s = ta.selectionStart, e = ta.selectionEnd;
    const sel = value.slice(s, e) || 'Text';
    const next = value.slice(0, s) + before + sel + after + value.slice(e);
    onChange(next);
    setTimeout(() => { ta.focus(); ta.setSelectionRange(s + before.length, s + before.length + sel.length); }, 0);
  };

  const toList = () => {
    const ta = ref.current;
    if (!ta) return;
    const s = ta.selectionStart, e = ta.selectionEnd;
    const start = value.lastIndexOf('\n', s - 1) + 1;
    let end = value.indexOf('\n', e); if (end === -1) end = value.length;
    const block = value.slice(start, end);
    const converted = block.split('\n').map(l => (l.trim() && !l.trimStart().startsWith('- ')) ? `- ${l}` : l).join('\n');
    onChange(value.slice(0, start) + converted + value.slice(end));
    setTimeout(() => ta.focus(), 0);
  };

  const btn = "h-7 w-7 inline-flex items-center justify-center rounded-md border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200";

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <button type="button" className={btn} onClick={() => wrap('**')} title="Fett" data-testid="md-bold"><Bold className="h-3.5 w-3.5" /></button>
        <button type="button" className={btn} onClick={() => wrap('*')} title="Kursiv" data-testid="md-italic"><Italic className="h-3.5 w-3.5" /></button>
        <button type="button" className={btn} onClick={toList} title="Aufzählung" data-testid="md-list"><List className="h-3.5 w-3.5" /></button>
        <button type="button" className={`${btn} w-auto px-2 gap-1 ml-auto text-xs`} onClick={() => setPreview(p => !p)} data-testid="md-toggle-preview">
          {preview ? <><Pencil className="h-3.5 w-3.5" /> Bearbeiten</> : <><Eye className="h-3.5 w-3.5" /> Vorschau</>}
        </button>
      </div>
      {preview ? (
        <div className="w-full min-h-48 px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-gray-50 dark:bg-gray-900/50 overflow-auto" data-testid="md-preview">
          {value?.trim() ? <MarkdownContent text={value} /> : <span className="text-muted-foreground text-xs">Nichts zum Anzeigen</span>}
        </div>
      ) : (
        <textarea
          ref={ref}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full h-48 px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-gray-900 resize-y focus:outline-none focus:ring-2 focus:ring-primary/50"
          data-testid={testId}
        />
      )}
    </div>
  );
};

export default MarkdownField;
